import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  CalendarDays, 
  Plus, 
  Send, 
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Paperclip,
  Trash2,
  Eye
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtcDate } from '@/utils/dateUtils';

function StaffLeaveRequestPage() {
  const { t, isRTL } = useLanguage();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequestDialog, setShowNewRequestDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [recentNotifications, setRecentNotifications] = useState([]);
  
  const [formData, setFormData] = useState({
    subject: '',
    start_date: '',
    end_date: '',
    reason: '',
    leave_type: 'off_day'
  });
  const [attachmentFile, setAttachmentFile] = useState(null);

  useEffect(() => {
    fetchLeaveRequests();
    fetchAndMarkNotifications();
  }, []);

  const fetchAndMarkNotifications = async () => {
    try {
      // Fetch notifications
      const response = await axios.get(`${API}/staff/leave-notifications`, {
        withCredentials: true
      });
      setRecentNotifications(response.data.notifications || []);
      
      // Mark as read after a short delay
      if (response.data.unread_count > 0) {
        setTimeout(async () => {
          await axios.post(`${API}/staff/leave-notifications/mark-read`, {}, {
            withCredentials: true
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const response = await axios.get(`${API}/staff/leave-requests`, {
        withCredentials: true
      });
      setLeaveRequests(response.data.leave_requests || []);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
      showMessage('error', 'Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.start_date || !formData.end_date || !formData.reason) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }
    
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      showMessage('error', 'End date cannot be before start date');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/staff/leave-request`,
        formData,
        { withCredentials: true }
      );
      
      const leaveId = response.data.leave_request.leave_id;
      
      // Upload attachment if present
      if (attachmentFile) {
        setUploadingFile(true);
        const formDataFile = new FormData();
        formDataFile.append('file', attachmentFile);
        
        await axios.post(
          `${API}/staff/leave-request/${leaveId}/attachment`,
          formDataFile,
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      }
      
      showMessage('success', 'Leave request submitted successfully!');
      setShowNewRequestDialog(false);
      setFormData({
        subject: '',
        start_date: '',
        end_date: '',
        reason: '',
        leave_type: 'off_day'
      });
      setAttachmentFile(null);
      fetchLeaveRequests();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setSubmitting(false);
      setUploadingFile(false);
    }
  };

  const handleCancelRequest = async (leaveId) => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) return;
    
    try {
      await axios.delete(`${API}/staff/leave-request/${leaveId}`, {
        withCredentials: true
      });
      showMessage('success', 'Leave request cancelled');
      fetchLeaveRequests();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getLeaveTypeBadge = (type) => {
    const types = {
      'off_day': { label: 'Off Day', color: 'bg-blue-50 text-blue-700' },
      'sick_leave': { label: 'Sick Leave', color: 'bg-red-50 text-red-700' },
      'vacation': { label: 'Vacation', color: 'bg-purple-50 text-purple-700' },
      'personal': { label: 'Personal', color: 'bg-gray-50 text-gray-700' },
      'emergency': { label: 'Emergency', color: 'bg-orange-50 text-orange-700' }
    };
    const config = types[type] || { label: type, color: 'bg-gray-50 text-gray-700' };
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    return formatUtcDate(dateStr + 'T00:00:00');
  };

  return (
    <div className="space-y-6">
      {/* Recent Notifications Alert */}
      {recentNotifications.length > 0 && (
        <div className="space-y-2">
          {recentNotifications.map((notif) => (
            <div 
              key={notif.leave_id}
              className={`p-4 rounded-lg border flex items-start gap-3 ${
                notif.status === 'approved' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}
            >
              {notif.status === 'approved' ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`font-medium ${notif.status === 'approved' ? 'text-green-800' : 'text-red-800'}`}>
                  Your leave request &quot;{notif.subject}&quot; has been {notif.status}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(notif.start_date)} - {formatDate(notif.end_date)}
                </p>
                {notif.admin_remarks && (
                  <p className="text-sm mt-2 italic text-gray-700">
                    &quot;{notif.admin_remarks}&quot;
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg ${
          message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Requests</h1>
          <p className="text-gray-600">Apply for time off and track your leave requests</p>
        </div>
        <Button 
          onClick={() => setShowNewRequestDialog(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="new-leave-request-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Leave Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-blue-600" />
            My Leave Requests
          </CardTitle>
          <CardDescription>View and manage your leave requests</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : leaveRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No leave requests yet</p>
              <p className="text-sm mt-2">Click &quot;New Request&quot; to apply for time off</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date Range</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaveRequests.map((request) => (
                  <TableRow key={request.leave_id}>
                    <TableCell className="font-medium">{request.subject}</TableCell>
                    <TableCell>{getLeaveTypeBadge(request.leave_type)}</TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {formatDate(request.start_date)} - {formatDate(request.end_date)}
                      </span>
                    </TableCell>
                    <TableCell>{request.days_count}</TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowDetailDialog(true);
                          }}
                          data-testid={`view-request-${request.leave_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {request.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleCancelRequest(request.leave_id)}
                            data-testid={`cancel-request-${request.leave_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Request Dialog */}
      <Dialog open={showNewRequestDialog} onOpenChange={setShowNewRequestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              New Leave Request
            </DialogTitle>
            <DialogDescription>
              Submit a request for time off. Your manager will review it.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                placeholder="e.g., Family vacation, Medical appointment"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                data-testid="leave-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="leave_type">{t("leave.leave_type")}</Label>
              <Select
                value={formData.leave_type}
                onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
              >
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off_day">Off Day</SelectItem>
                  <SelectItem value="sick_leave">Sick Leave</SelectItem>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="personal">Personal Leave</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="leave-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date *</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="leave-end-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Textarea
                id="reason"
                placeholder="Please provide details about your leave request..."
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={3}
                data-testid="leave-reason-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="attachment">Attachment (Optional)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="attachment"
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                  onChange={(e) => setAttachmentFile(e.target.files[0])}
                  className="flex-1"
                  data-testid="leave-attachment-input"
                />
                {attachmentFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAttachmentFile(null)}
                  >
                    <XCircle className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
              {attachmentFile && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" />
                  {attachmentFile.name}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewRequestDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || uploadingFile}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-leave-request-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadingFile ? 'Uploading...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Leave Request Details
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{selectedRequest.subject}</h3>
                  <div className="flex gap-2 mt-1">
                    {getLeaveTypeBadge(selectedRequest.leave_type)}
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Start Date</p>
                  <p className="font-medium">{formatDate(selectedRequest.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End Date</p>
                  <p className="font-medium">{formatDate(selectedRequest.end_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Days</p>
                  <p className="font-medium">{selectedRequest.days_count} day(s)</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Submitted</p>
                  <p className="font-medium">
                    {formatUtcDate(selectedRequest.created_at)}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">Reason</p>
                <p className="text-gray-700 bg-white p-3 rounded border">{selectedRequest.reason}</p>
              </div>

              {selectedRequest.attachment_url && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Attachment</p>
                  <a
                    href={selectedRequest.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <Paperclip className="w-4 h-4" />
                    View Attachment
                  </a>
                </div>
              )}

              {selectedRequest.status === 'approved' && selectedRequest.is_paid != null && (
                <div className={`p-3 rounded-lg border ${
                  selectedRequest.is_paid
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  <p className="text-xs opacity-70 mb-0.5">Salary treatment</p>
                  <p className="font-semibold text-sm">
                    {selectedRequest.is_paid ? 'Paid leave' : 'Unpaid leave'}
                  </p>
                </div>
              )}

              {selectedRequest.admin_remarks && (
                <div className={`p-4 rounded-lg ${
                  selectedRequest.status === 'approved' ? 'bg-green-50 border border-green-200' :
                  selectedRequest.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                  'bg-gray-50'
                }`}>
                  <p className="text-xs text-gray-500 mb-1">Admin Remarks</p>
                  <p className="font-medium">{selectedRequest.admin_remarks}</p>
                  {selectedRequest.reviewed_at && (
                    <p className="text-xs text-gray-500 mt-2">
                      Reviewed on {formatUtcDate(selectedRequest.reviewed_at)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StaffLeaveRequestPage;
